<template>
  <div class="mobile-category-tabs">
    <!-- Breadcrumb / Current Selection -->
    <div class="category-header" v-if="categorySearch.category">
      <button class="back-btn" @click="goBack">
        <font-awesome-icon icon="fa-solid fa-arrow-left" />
      </button>
      <div class="breadcrumb">
        <span class="breadcrumb-item" @click="selectMainCategory" v-if="categorySearch.category">
          {{ categorySearch.category}}
        </span>
        <span class="breadcrumb-separator" v-if="categorySearch.subCategory">›</span>
        <span class="breadcrumb-item" @click="selectSubCategory" v-if="categorySearch.subCategory">
          {{ categorySearch.subCategory }}
        </span>
        <span class="breadcrumb-separator" v-if="categorySearch.sub2Category">›</span>
        <span class="breadcrumb-item active" v-if="categorySearch.sub2Category">
          {{ categorySearch.sub2Category }}
        </span>
      </div>
      <button class="reset-btn" @click="clearCategory">
        <font-awesome-icon icon="fa-solid fa-home" />
      </button>
    </div>

    <!-- Category Tabs -->
    <div class="tabs-container" ref="tabsContainer" @scroll="handleScroll">
      <button
        v-for="category in selectedCategories"
        :key="category.name"
        class="category-tab"
        @click="selectCategory(category.name)"
      >
        {{ category.name }}
      </button>
    </div>

    <!-- Scroll Indicators -->
    <div class="scroll-indicators" v-if="showScrollIndicators">
      <button 
        class="scroll-indicator left" 
        v-if="canScrollLeft"
        @click="scrollLeft"
      >
        <font-awesome-icon icon="fa-solid fa-chevron-left" />
      </button>
      <button 
        class="scroll-indicator right" 
        v-if="canScrollRight"
        @click="scrollRight"
      >
        <font-awesome-icon icon="fa-solid fa-chevron-right" />
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MobileCategoryTabs',
  data() {
    return {
      category: undefined,
      subCategory: undefined,
      subSubCategory: undefined,
      canScrollLeft: false,
      canScrollRight: false,
      showScrollIndicators: false
    };
  },
  computed: {
    categorySearch() {
      return this.$store.getters.getSearchCategories;
    },
    categories() {
      return this.$store.getters.getCategories;
    },
    selectedCategories() {
      if (this.category && this.subCategory) {
        let main = this.categories.find(
          (cat) => cat.name.localeCompare(this.category, undefined, { sensitivity: 'base' }) === 0
        );
        let sub = main?.children.find(
          (cat) => cat.name.localeCompare(this.subCategory, undefined, { sensitivity: 'base' }) === 0
        );
        return sub?.children || main?.children || [];
      } else if (this.category) {
        return this.categories.find(
          (cat) => cat.name.localeCompare(this.category, undefined, { sensitivity: 'base' }) === 0
        )?.children || [];
      } else {
        return this.categories;
      }
    }
  },
  mounted() {
    this.searchProducts();
    this.$nextTick(() => {
      this.checkScroll();
    });
  },
  updated() {
    this.$nextTick(() => {
      this.checkScroll();
    });
  },
  methods: {
    clearCategory() {
      this.category = undefined;
      this.subCategory = undefined;
      this.subSubCategory = undefined;
      this.searchProducts();
    },
    goBack() {
      if (this.category && this.subCategory && this.subSubCategory) {
        this.subSubCategory = undefined;
      } else if (this.category && this.subCategory) {
        this.subCategory = undefined;
      } else {
        this.category = undefined;
      }
      this.searchProducts();
    },
    selectMainCategory() {
      this.$store.commit('setLimit', 30);
      this.subSubCategory = undefined;
      this.subCategory = undefined;
      this.searchProducts();
    },
    selectSubCategory() {
      this.$store.commit('setLimit', 30);
      this.subSubCategory = undefined;
      this.searchProducts();
    },
    selectCategory(categoryName) {
      this.$store.commit('setLimit', 30);
      
      if (this.category && this.subCategory) {
        this.subSubCategory = categoryName;
      } else if (this.category) {
        this.subCategory = categoryName;
      } else {
        this.category = categoryName;
      }
      
      this.searchProducts();
    },
    searchProducts() {
      if (this.category) {
        let searchQuery = {
          category: this.category || '',
          subCategory: this.subCategory || '',
          sub2Category: this.subSubCategory || ''
        };
        this.$store.commit('setSearchCategories', searchQuery);
        this.$store.dispatch('searchStock', searchQuery);
      } else {
        this.$store.dispatch('getFastLine');
      }
    },
    handleScroll() {
      this.checkScroll();
    },
    checkScroll() {
      const container = this.$refs.tabsContainer;
      if (!container) return;
      
      this.canScrollLeft = container.scrollLeft > 0;
      this.canScrollRight = container.scrollLeft < (container.scrollWidth - container.clientWidth - 1);
      this.showScrollIndicators = container.scrollWidth > container.clientWidth;
    },
    scrollLeft() {
      this.$refs.tabsContainer.scrollBy({ left: -200, behavior: 'smooth' });
    },
    scrollRight() {
      this.$refs.tabsContainer.scrollBy({ left: 200, behavior: 'smooth' });
    }
  }
};
</script>

<style scoped lang="scss">
.mobile-category-tabs {
  position: sticky;
  top: 56px; // Below nav bar
  z-index: 997;
  background: var(--bg-primary);
  border-bottom: var(--card-border);
  box-shadow: var(--card-shadow);
}

.category-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(var(--accent-electric-blue-rgb), 0.08);
  border-bottom: 1px solid rgba(var(--accent-electric-blue-rgb), 0.2);
  
  .back-btn,
  .reset-btn {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: var(--button-radius);
    background: transparent;
    color: var(--accent-primary);
    border: 1px solid var(--accent-primary);
    font-size: 14px;
    cursor: pointer;
    transition: var(--transition-default);
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:active {
      transform: scale(0.95);
      background: rgba(var(--accent-electric-blue-rgb), 0.15);
    }
  }
  
  .breadcrumb {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 600;
    overflow-x: auto;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;
    
    &::-webkit-scrollbar {
      display: none;
    }
    
    .breadcrumb-item {
      color: var(--accent-primary);
      cursor: pointer;
      transition: var(--transition-default);
      
      &:active {
        opacity: 0.7;
      }
      
      &.active {
        color: var(--text-primary);
        cursor: default;
      }
    }
    
    .breadcrumb-separator {
      color: var(--text-secondary);
    }
  }
}

.tabs-container {
  display: flex;
  gap: 8px;
  padding: 12px;
  overflow-x: auto;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  .category-tab {
    flex-shrink: 0;
    padding: 12px 20px;
    min-width: 80px;
    height: 48px;
    border-radius: var(--button-radius);
    background: var(--card-bg);
    color: var(--text-primary);
    border: var(--card-border);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: var(--transition-default);
    box-shadow: var(--card-shadow);
    white-space: nowrap;
    
    &:active {
      transform: scale(0.97);
      background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-primary-dark) 100%);
      color: white;
      border-color: transparent;
      box-shadow: 0 4px 12px rgba(var(--accent-electric-blue-rgb), 0.4);
    }
  }
}

.scroll-indicators {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  pointer-events: none;
  display: flex;
  justify-content: space-between;
  padding: 0 4px;
  
  .scroll-indicator {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(var(--accent-electric-blue-rgb), 0.9);
    color: white;
    border: none;
    font-size: 12px;
    cursor: pointer;
    pointer-events: auto;
    box-shadow: var(--card-shadow);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: var(--transition-default);
    
    &:active {
      transform: scale(0.9);
    }
    
    &.left {
      margin-left: 4px;
    }
    
    &.right {
      margin-right: 4px;
    }
  }
}
</style>
